import React, { useEffect, useState } from 'react';
import { Form, InputNumber, Button, Spin } from 'antd';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { type AppDispatch } from '../../redux/store';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../components/ToastComponent/store/toastMiddleware';
import {
  fetchOrganizationSettings,
  updateOrganizationSettings,
  type OrganizationSettings,
} from './organizationSettingsApi';

const GENERIC_MESSAGE = 'Something went wrong. Please try again.';

export default function OrganizationSettingsIndex(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [form] = Form.useForm<OrganizationSettings>();
  const [isBusy, setIsBusy] = useState(false);

  const handleError = (error: any) => {
    if (error?.response?.status === 401) {
      window.localStorage.clear();
      navigate('/sign-in');
      return;
    }
    const message = error?.response?.data?.message;
    dispatch(
      showErrorMessage(typeof message === 'string' ? message : GENERIC_MESSAGE)
    );
  };

  useEffect(() => {
    const load = async () => {
      setIsBusy(true);
      try {
        form.setFieldsValue(await fetchOrganizationSettings());
      } catch (error: any) {
        handleError(error);
      } finally {
        setIsBusy(false);
      }
    };
    void load();
  }, []);

  const onFinish = async (values: OrganizationSettings) => {
    if (isBusy) return;
    setIsBusy(true);
    try {
      const res = await updateOrganizationSettings({
        netWeightReductionPercentage: Number(
          values.netWeightReductionPercentage
        ),
      });
      form.setFieldsValue(res.settings);
      dispatch(
        showSuccessMessage(res?.message ?? 'Settings updated successfully')
      );
    } catch (error: any) {
      handleError(error);
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="settings__card settings__card--narrow">
      <Spin spinning={isBusy} fullscreen />
      <p className="settings__title">Organization</p>
      <p className="settings__subtitle">
        Settings that apply to every branch of the organization.
      </p>

      <Form
        form={form}
        layout="vertical"
        requiredMark={(label, { required }) => (
          <>
            {label}
            {required && <span className="settings__req">*</span>}
          </>
        )}
        onFinish={onFinish}
      >
        <Form.Item
          label="Net weight reduction percentage"
          name="netWeightReductionPercentage"
          rules={[
            { required: true, message: 'Please enter a percentage' },
            {
              type: 'number',
              min: 0,
              max: 100,
              message: 'Enter a percentage between 0 and 100',
            },
          ]}
        >
          <InputNumber
            className="settings__input"
            style={{ width: '100%' }}
            min={0}
            max={100}
            step={0.5}
            addonAfter="%"
            placeholder="e.g. 3"
          />
        </Form.Item>
        <p className="settings__hint">
          In the loan form, net weight = (gross weight - stone weight) less this
          percentage. Only the admin can change a calculated net weight.
        </p>

        <div className="settings__actions">
          <Button
            htmlType="submit"
            disabled={isBusy}
            className="settings__button settings__button--primary"
          >
            Save
          </Button>
        </div>
      </Form>
    </div>
  );
}
